import { useSignal } from '@preact/signals-react';
import { useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import appConfig from '../../../../config/app-config';
import MaskEdit from '../../../organisms/MaskEdit';
import TextEdit from '../../../molecules/TextEdit';
import Dialog from '../../../organisms/Dialog';
import TemplateGroupSelector from './TemplateGroupSelector';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const BASE_URL = COMMUNICATION_ENV.address;
const API_KEY = COMMUNICATION_ENV.key;

const PHONE = 'PHONE'

interface ContactPointModalFormProps {
  contactId: string;
  contactPointId: string;
  type: 'PHONE' | 'EMAIL';
  currentValue: string;
  isActive: boolean;
  isPrimary: boolean;
  isWhatsapp?: boolean;
  observation?: string;
  templates?: string[];
  templateGroups?: string[];
  onClose: () => void;
  onSuccess: (updatedContactPoint: any) => void;
}

export default function ContactPointModalForm({
  contactId,
  contactPointId,
  type,
  currentValue,
  isActive,
  isPrimary,
  isWhatsapp = false,
  observation = '',
  templates = [],
  templateGroups: initialTemplateGroups = [],
  onClose,
  onSuccess,
}: ContactPointModalFormProps) {
  const isSending = useSignal(false);
  const formData = useSignal({
    value: currentValue,
    isActive,
    isPrimary,
    isWhatsapp,
    notes: observation,
  });
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>(templates);
  const [phoneType, setPhoneType] = useState<'mobile' | 'landline'>('mobile');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const availableTemplates = useSignal<Array<{ id: string; name: string; groupName?: string }>>([]);
  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>(initialTemplateGroups);
  const [customMode, setCustomMode] = useState(initialTemplateGroups.length === 0 && templates.length > 0);

  useEffect(() => {
    // Sincronizar com props quando mudarem
    formData.value = {
      value: currentValue,
      isActive,
      isPrimary,
      isWhatsapp,
      notes: observation,
    };
    setSelectedTemplates(templates);
  }, [currentValue, isActive, isPrimary, isWhatsapp, observation, templates, formData]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingTemplates(true);
      try {
        const token = localStorage.getItem('authToken');
        const headers: Record<string, string> = { 'x-api-key': API_KEY || '' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const fetches: Promise<Response>[] = [
          fetch(`${BASE_URL}/templates/page-list`, { headers }),
          fetch(`${BASE_URL}/templates/group-names`, { headers }),
        ];
        const isCreating = !contactPointId;
        if (isCreating && initialTemplateGroups.length === 0) {
          fetches.push(fetch(`${BASE_URL}/contacts/contact-points/page-form`, { headers }));
        }
        const responses = await Promise.all(fetches);
        const [templatesRes, groupsRes] = responses;
        let loadedTemplates: Array<{ id: string; name: string; groupName?: string }> = [];
        let loadedGroups: string[] = [];
        if (templatesRes.ok) {
          const data = await templatesRes.json();
          loadedTemplates = data.list || [];
          availableTemplates.value = loadedTemplates;
        }
        if (groupsRes.ok) {
          const data = await groupsRes.json();
          loadedGroups = data.list || [];
          setGroupNames(loadedGroups);
        }
        if (isCreating && initialTemplateGroups.length === 0 && responses[2]?.ok) {
          const data = await responses[2].json();
          const defaults = data.defaultTemplateGroups || data.groups || [];
          if (defaults.length > 0) {
            setSelectedGroups(defaults);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getResolvedTemplates = () => {
    if (customMode) return selectedTemplates;
    return availableTemplates.value
      .filter(t => t.groupName && selectedGroups.includes(t.groupName))
      .map(t => t.name);
  };

  const buildPayload = () => {
    const isCreating = !contactPointId;
    const base: any = {
      type,
      value: formData.value.value,
      isActive: formData.value.isActive,
      isPrimary: formData.value.isPrimary,
      notes: formData.value.notes,
    };
    if (type === PHONE) base.isWhatsapp = formData.value.isWhatsapp;

    if (customMode) {
      base.templates = selectedTemplates;
      base.templateGroups = [];
    } else {
      base.templateGroups = selectedGroups;
    }

    if (isCreating) return base;

    // For update, only send changed fields
    const payload: any = {};
    if (formData.value.value !== currentValue) payload.value = formData.value.value;
    if (formData.value.isActive !== isActive) payload.isActive = formData.value.isActive;
    if (formData.value.isPrimary !== isPrimary) payload.isPrimary = formData.value.isPrimary;
    if (type === PHONE) payload.isWhatsapp = formData.value.isWhatsapp;
    if (formData.value.notes !== observation) payload.notes = formData.value.notes;
    if (customMode) {
      if (JSON.stringify(selectedTemplates) !== JSON.stringify(templates)) {
        payload.templates = selectedTemplates;
        payload.templateGroups = [];
      }
    } else {
      payload.templateGroups = selectedGroups;
    }
    return payload;
  };

  const submitContactPoint = async (isCreating: boolean, payload: any) => {
    const url = isCreating
      ? `${BASE_URL}/contacts/${contactId}/contact-points`
      : `${BASE_URL}/contacts/${contactId}/contact-points/${contactPointId}`;

    const session = await (await import('aws-amplify')).Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const response = await fetch(url, {
      method: isCreating ? 'POST' : 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY || '',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Erro ao ${isCreating ? 'criar' : 'atualizar'} contact point`);
    }
    return response.json();
  };

  const handleSubmit = async () => {
    if (!formData.value.value.trim()) {
      toast.error('Valor é obrigatório');
      return;
    }

    isSending.value = true;
    const isCreating = !contactPointId;
    const label = isCreating ? 'criar' : 'atualizar';

    try {
      const result = await submitContactPoint(isCreating, buildPayload());
      toast.success(`Contact point ${isCreating ? 'criado' : 'atualizado'} com sucesso!`);
      onSuccess(result);
      onClose();
    } catch (error: any) {
      toast.error(error.message || `Erro ao ${label} contact point`);
    } finally {
      isSending.value = false;
    }
  };

  const toggleTemplate = (templateId: string) => {
    setSelectedTemplates(prev =>
      prev.includes(templateId) ? prev.filter(id => id !== templateId) : [...prev, templateId]
    );
  };

  const toggleGroup = (group: string) => {
    setSelectedGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    );
  };

  return (
    <Dialog
      id="contact-point-modal"
      show={true}
      onClose={onClose}
      header={`${contactPointId ? 'Editar' : 'Novo'} ${type === PHONE ? 'Telefone' : 'Email'}`}
      body={
        <div className="p-6 space-y-4">
          {/* Two columns layout */}
          <div className="grid grid-cols-3 gap-6">
            {/* Left column - 2/3 width */}
            <div className="col-span-2 space-y-4">
              {/* Value */}
              {type === PHONE ? (
                <MaskEdit
                  key={phoneType}
                  label="Telefone"
                  mask={phoneType === 'mobile' ? '(__) _____-____' : '(__) ____-____'}
                  replacement={{ _: /\d/ }}
                  value={formData.value.value}
                  onChange={(e) => (formData.value = { ...formData.value, value: e.target.value })}
                  placeholder=""
                  prefix={
                    <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-slate-700 rounded-md mr-2">
                      <button type="button" onClick={() => setPhoneType('mobile')} className={`px-2 py-1 text-xs rounded transition-colors ${phoneType === 'mobile' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Celular</button>
                      <button type="button" onClick={() => setPhoneType('landline')} className={`px-2 py-1 text-xs rounded transition-colors ${phoneType === 'landline' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Fixo</button>
                    </div>
                  }
                />
              ) : (
                <TextEdit
                  label="Email"
                  value={formData.value.value}
                  onChange={(e) => (formData.value = { ...formData.value, value: e.target.value })}
                  placeholder=""
                />
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Observação
                </label>
                <textarea
                  value={formData.value.notes}
                  onChange={(e) => (formData.value = { ...formData.value, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-700 dark:text-white resize-none"
                  placeholder=""
                />
              </div>
            </div>

            {/* Right column - Toggles */}
            <div className="space-y-4">
              {/* WhatsApp (apenas para telefone) */}
              {type === PHONE && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.value.isWhatsapp}
                      onChange={(e) => (formData.value = { ...formData.value, isWhatsapp: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-green-500 peer-focus:ring-2 peer-focus:ring-green-300 transition-colors"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                  </div>
                  <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <FaWhatsapp className="text-green-500" />
                    WhatsApp
                  </span>
                </label>
              )}

              {/* Primary */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.value.isPrimary}
                    onChange={(e) => (formData.value = { ...formData.value, isPrimary: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-blue-500 peer-focus:ring-2 peer-focus:ring-blue-300 transition-colors"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Principal
                </span>
              </label>

              {/* Active */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.value.isActive}
                    onChange={(e) => (formData.value = { ...formData.value, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-blue-500 peer-focus:ring-2 peer-focus:ring-blue-300 transition-colors"></div>
                  <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Ativo
                </span>
              </label>
            </div>
          </div>

          {/* Templates - Full width */}
          <TemplateGroupSelector
            groupNames={groupNames}
            selectedGroups={selectedGroups}
            onToggleGroup={toggleGroup}
            availableTemplates={availableTemplates.value}
            selectedTemplates={selectedTemplates}
            onToggleTemplate={toggleTemplate}
            customMode={customMode}
            onToggleMode={() => setCustomMode(!customMode)}
            loading={loadingTemplates}
          />
        </div>
      }
      footer={
        <div className="flex gap-3">
          <button onClick={onClose} disabled={isSending.value} className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600">Cancelar</button>
          <button onClick={handleSubmit} disabled={isSending.value} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {isSending.value ? (contactPointId ? 'Atualizando...' : 'Salvando...') : (contactPointId ? 'Atualizar' : 'Salvar')}
          </button>
        </div>
      }
    />
  );
}
